// Frozen. Mount once, inside the existing /supplement-check page, below "Your last plan". No props. Do not edit text or logic.
import React, { useCallback, useEffect, useState } from "react";
import { authFetch } from "./planApi";

const ORANGE = "var(--supple-orange, #e8651a)", INK = "var(--supple-ink, #1c1c1c)", CARD = "var(--supple-card, #ffffff)", SOFT = "var(--supple-soft, #f3e3dc)";
const S = {
  wrap: { display: "grid", gap: 16, margin: "24px 0", color: INK },
  card: { background: CARD, borderRadius: 14, padding: 16, display: "grid", gap: 10, border: "1px solid rgba(0,0,0,.08)" },
  h: { margin: 0, fontSize: 20 }, label: { fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: ORANGE, fontWeight: 700 },
  row: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 },
  btn: (on) => ({ minHeight: 44, borderRadius: 10, border: `2px solid ${on ? ORANGE : "rgba(0,0,0,.2)"}`, background: on ? ORANGE : "transparent", color: on ? "#fff" : "inherit", fontSize: 15, fontWeight: 600, cursor: "pointer", padding: "8px 6px" }),
  main: { minHeight: 48, borderRadius: 12, border: "none", background: INK, color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer", padding: "10px 16px" },
  ghost: { minHeight: 44, borderRadius: 10, border: `2px solid ${INK}`, background: "transparent", color: "inherit", fontSize: 15, fontWeight: 600, cursor: "pointer", padding: "8px 14px" },
  msg: { background: SOFT, borderRadius: 10, padding: "8px 12px", margin: 0 },
};
const DIET = [["on_track", "On track"], ["partly", "Partly"], ["off", "Off"]];
const EXER = [["all", "All sessions"], ["most", "Most"], ["few", "Few"]];
const SUPP = [["most", "Most days"], ["some", "Some days"], ["few", "Few days"]];

function Pick({ id, options, value, onChange }) {
  return <div style={S.row} data-testid={`eight-week-${id}-options`}>{options.map(([v, t]) => <button key={v} type="button" style={S.btn(value === v)} onClick={() => onChange(v)} data-testid={`eight-week-${id}-option-${v}`}>{t}</button>)}</div>;
}

export default function EightWeekPlan() {
  const [status, setStatus] = useState("loading"); // loading | hidden | start | ready
  const [d, setD] = useState(null);
  const [track, setTrack] = useState(null);
  const [ci, setCi] = useState({ diet: null, exercise: null, supplements: null });
  const [msgs, setMsgs] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await authFetch("/api/plan/today");
    if (r.status === 404) return setStatus("start");
    if (!r.ok) return setStatus("hidden");           // 401 (logged out) or 409 (no saved plan): show nothing
    setD(await r.json()); setStatus("ready");
    const p = await authFetch("/api/plan/checkin-prefill");
    if (p.ok) { const j = await p.json(); setCi((c) => ({ ...c, supplements: c.supplements || j.supplements })); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (status === "loading" || status === "hidden") return null;

  const start = async () => { setBusy(true); await authFetch("/api/plan/start", { method: "POST", body: JSON.stringify({ track }) }); setBusy(false); load(); };
  const tick = async (label, taken) => {
    setD((x) => ({ ...x, supplements: x.supplements.map((s) => (s.label === label ? { ...s, taken } : s)) }));
    await authFetch("/api/plan/tick", { method: "POST", body: JSON.stringify({ label, taken }) });
    const p = await authFetch("/api/plan/checkin-prefill"); if (p.ok) { const j = await p.json(); setCi((c) => ({ ...c, supplements: j.supplements })); }
  };
  const save = async () => {
    setBusy(true);
    const r = await authFetch("/api/plan/checkin", { method: "POST", body: JSON.stringify(ci) });
    setBusy(false); if (r.ok) { const j = await r.json(); setMsgs(j.messages); setCi({ diet: null, exercise: null, supplements: null }); load(); }
  };
  const pdf = async () => {
    const r = await authFetch("/api/plan/pdf"); if (!r.ok) return;
    const url = URL.createObjectURL(await r.blob()); const a = document.createElement("a");
    a.href = url; a.download = "Supple_8-week_plan.pdf"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  if (status === "start") return (
    <section style={S.wrap} aria-label="Your 8-week plan" data-testid="eight-week-plan-start">
      <h2 style={S.h} data-testid="eight-week-plan-start-heading">Your 8-week plan</h2>
      <div style={S.card} data-testid="eight-week-track-card">
        <strong data-testid="eight-week-track-question">Do you go to a gym?</strong>
        <div style={S.row} data-testid="eight-week-track-options">
          <button type="button" style={S.btn(track === "gym")} onClick={() => setTrack("gym")} data-testid="eight-week-track-gym-button">Yes, I go to a gym</button>
          <button type="button" style={S.btn(track === "home")} onClick={() => setTrack("home")} data-testid="eight-week-track-home-button">No, I'll start at home</button>
        </div>
        <button type="button" style={{ ...S.main, opacity: track ? 1 : 0.5 }} disabled={!track || busy} onClick={start} data-testid="eight-week-start-button">Start my 8 weeks</button>
      </div>
    </section>
  );

  return (
    <section style={S.wrap} aria-label="Your 8-week plan" data-testid="eight-week-plan-ready">
      <h2 style={S.h} data-testid="eight-week-plan-heading">Your 8-week plan</h2>
      {d.done ? <div style={S.card} data-testid="eight-week-plan-complete"><strong>You finished 8 weeks.</strong></div> : (
        <>
          <div style={S.card} data-testid="eight-week-today-card">
            <span style={S.label} data-testid="eight-week-current-week">Week {d.week} of 8 · Today</span>
            <span style={S.label} data-testid="eight-week-eat-label">Eat</span>
            {d.eat.map((t, i) => <div key={i} data-testid={`eight-week-eat-item-${i}`}>• {t}</div>)}
            <span style={S.label} data-testid="eight-week-move-label">Move</span>
            {d.moves ? (<><strong data-testid="eight-week-move-day">{d.moves.label}</strong>{d.moves.moves.map((m, i) => <div key={m.id} data-testid={`eight-week-move-item-${i}`}>• {m.title}: {m.reps}</div>)}{d.moves.note ? <div data-testid="eight-week-move-note">{d.moves.note}</div> : null}</>) : <div data-testid="eight-week-rest-day">Rest day. An easy walk is fine.</div>}
            <span style={S.label} data-testid="eight-week-supplements-label">Supplements</span>
            {d.supplements.map((s, i) => <button key={s.label} type="button" style={{ ...S.btn(s.taken), textAlign: "left" }} onClick={() => tick(s.label, !s.taken)} data-testid={`eight-week-supplement-tick-${i}`}>{s.taken ? "✓ " : ""}{s.label}: Taken today</button>)}
          </div>
          <div style={S.card} data-testid="eight-week-checkin-card">
            <span style={S.label} data-testid="eight-week-checkin-label">Weekly check-in</span>
            <strong data-testid="eight-week-diet-question">Diet</strong><Pick id="diet" options={DIET} value={ci.diet} onChange={(v) => setCi({ ...ci, diet: v })} />
            <strong data-testid="eight-week-exercise-question">Exercise</strong><Pick id="exercise" options={EXER} value={ci.exercise} onChange={(v) => setCi({ ...ci, exercise: v })} />
            <strong data-testid="eight-week-supplements-question">Supplements</strong><Pick id="supplements" options={SUPP} value={ci.supplements} onChange={(v) => setCi({ ...ci, supplements: v })} />
            <button type="button" style={{ ...S.main, opacity: ci.diet && ci.exercise ? 1 : 0.5 }} disabled={!(ci.diet && ci.exercise) || busy} onClick={save} data-testid="eight-week-checkin-save-button">Save check-in</button>
          </div>
        </>
      )}
      {msgs ? <div style={S.card} data-testid="eight-week-checkin-messages">{msgs.map((m, i) => <p key={i} style={S.msg} data-testid={`eight-week-checkin-message-${i}`}>{m}</p>)}<strong data-testid="eight-week-message-week">Week {d.week} of 8</strong></div> : null}
      <button type="button" style={S.ghost} onClick={pdf} data-testid="eight-week-pdf-button">Download my 8-week plan (PDF)</button>
    </section>
  );
}
